//@flow
import * as Immutable from 'immutable';

import EntityTypeEnum from 'common/enums/EntityTypeEnum';

import {
    validatable,
    validate,
    validateAll,
    validateId,
    softValidateAll
} from 'common/utils/ValidationUtil';

import { MasterTransporter } from 'common/models/flow/MasterTransporter';
import type {
    MasterTransporterModelType,
    StatusEnum
} from 'common/models/flow/MasterTransporter';

import { ContactId } from './ContactId';
import { Contact } from './Contact';
import { TeamId } from './TeamId';
import { EmailAddress } from './EmailAddress';
import { PhoneNumber } from './PhoneNumber';

import { StructuredAddress } from './StructuredAddress';

import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';

import DataUtils from 'common/utils/DataUtils';
import RegExUtil from 'common/utils/RegExUtil';
import I18nHelper from 'common/helpers/I18nHelper';

import { Document } from './Document';

import { AuditMetadata } from './AuditMetadata';
import { ENTITY_NAME_ID } from 'common/constants/entity/transporterMaster';

const translationsNamespace = ['entityManagement', 'validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

export const TransporterTypeType = new EntityTypeEnum({
    OWNED_FLEET: 'OWNED_FLEET',
    OUTSOURCED_FLEET: 'OUTSOURCED_FLEET',
    THIRD_PARTY_FLEET: 'THIRD_PARTY_FLEET'
});

export const IsActiveEnum = new EntityTypeEnum({
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE'
});

const validateBoolean = (value: any, isRequired: boolean) => {
    if (
        (!DataUtils.isEmpty(value) || isRequired) &&
        !DataUtils.isBoolean(value)
    ) {
        return nsTranslate('validations:booleanValueValidation', {
            positiveLabel: nsTranslate(
                'entityManagement:transporterForm.boolean.yes'
            ),
            negativeLabel: nsTranslate(
                'entityManagement:transporterForm.boolean.no'
            )
        });
    }
};

@validatable
class InternalMasterTransporter extends MasterTransporter {
    static fromJS(json: MasterTransporterModelType): InternalMasterTransporter {
        const state: Object = Object.assign({}, json);
        state.contactId = state.contactId
            ? ContactId.fromJS(state.contactId)
            : state.contactId;
        state.contact = state.contact
            ? Contact.fromJS(state.contact)
            : state.contact;
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.services = Immutable.List(state.services);
        state.address = StructuredAddress.fromJS(state.address);
        state.emailAddress = state.emailAddress
            ? EmailAddress.fromJS(state.emailAddress)
            : state.emailAddress;
        state.additionalEmailAddresses = state.additionalEmailAddresses
            ? Immutable.List(state.additionalEmailAddresses).map(item =>
                  EmailAddress.fromJS(item)
              )
            : state.additionalEmailAddresses;
        state.phoneNumber = state.phoneNumber
            ? PhoneNumber.fromJS(state.phoneNumber)
            : state.phoneNumber;
        state.additionalPhoneNumbers = Immutable.List(
            state.additionalPhoneNumbers
        ).map(item => PhoneNumber.fromJS(item));
        state.documents = Immutable.List(state.documents).map(item =>
            Document.fromJS(item)
        );
        state.customProperties = Immutable.Map(state.customProperties);
        state.auditMetadata = AuditMetadata.fromJS(state.auditMetadata);
        return new this(Immutable.Map(state));
    }

    @validate
    setId(id: string): this {
        return super.setId(id);
    }
    validateId() {
        return validateId(this.id);
    }

    @validate
    setCode(code: string): this {
        return super
            .setCode(code)
            .setId(!DataUtils.isEmpty(code) ? RegExUtil.conformToId(code) : '');
    }

    validateCode() {
        const code = this.code || this.id;
        if (DataUtils.isEmpty(code)) {
            return nsTranslate('entityValidationErrors.codeEmpty');
        }
        if (code.trim().length < 2) {
            return nsTranslate('entityValidationErrors.codeLessThan2');
        }
        if (code.trim().length > 32) {
            return nsTranslate('entityValidationErrors.codeGreaterThan32');
        }
    }

    @validate
    setName(name: string): this {
        return super.setName(name);
    }

    validateName() {
        if (
            DataUtils.isEmpty(this.name) ||
            DataUtils.isEmpty(this.name.trim())
        ) {
            return nsTranslate('entityValidationErrors.nameEmpty', {
                entityNameId: ENTITY_NAME_ID
            });
        }
    }

    @validate
    setRegisteredName(registeredName: string): this {
        return super.setRegisteredName(registeredName);
    }

    validateRegisteredName() {
        if (
            DataUtils.isEmpty(this.registeredName) ||
            DataUtils.isEmpty(this.registeredName.trim())
        ) {
            return nsTranslate(
                'transporterValidationErrors.emptyRegisterName',
                {
                    entityNameId: ENTITY_NAME_ID
                }
            );
        }
    }

    @validate
    setIsActive(isActive: StatusEnum): this {
        return super.setIsActive(isActive);
    }

    validateIsActive() {
        return validateBoolean(this.isActive, true);
    }

    @validate
    setType(type: string): this {
        return super.setType(type);
    }

    validateType() {
        let validationMessage = null;
        const transporterTypeValues = TransporterTypeType.values();
        if (
            !this.type ||
            !this.type.trim() ||
            !transporterTypeValues.includes(this.type)
        ) {
            validationMessage = nsTranslate(
                'entityValidationErrors.typeEnumValidation',
                {
                    typeList: transporterTypeValues.join(', '),
                    entityNameId: ENTITY_NAME_ID
                }
            );
        }
        return validationMessage;
    }

    @validate
    setPhoneNumber(phoneNumber: PhoneNumber): this {
        return super.setPhoneNumber(phoneNumber);
    }

    validatePhoneNumber() {
        return (
            this.phoneNumber &&
            !DataUtils.isEmpty(this.phoneNumber.phoneNumber) &&
            this.phoneNumber.validatePhoneNumber()
        );
    }

    setContact(contact: ?Contact): this {
        let updatedTransporter = super.setContact(contact);
        if (contact) {
            updatedTransporter = updatedTransporter.setContactId(
                ContactId.fromJS({
                    clientId: contact.clientId,
                    contactId: contact.id,
                    version: contact.version
                })
            );
        } else {
            updatedTransporter = updatedTransporter.setContactId(null);
        }
        return updatedTransporter;
    }

    @validate
    setTeams(teams: Immutable.List<TeamId>): this {
        return super.setTeams(teams);
    }

    validateTeams() {
        if (DataUtils.isEmpty(this.teams) || this.teams.size <= 0) {
            return nsTranslate('transporterValidationErrors.teamEmpty');
        }
    }

    setDocuments(documents: Immutable.List<Document>): this {
        return super.setDocuments(documents);
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return this.documents.some(currentDocument =>
            currentDocument.hasValidationErrors()
        );
    }

    get contactId(): ?ContactId {
        return this._state.get('contactId');
    }

    get contact(): ?Contact {
        return this._state.get('contact');
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    get emailAddress(): ?EmailAddress {
        return this._state.get('emailAddress');
    }

    get additionalEmailAddresses(): ?Immutable.List<EmailAddress> {
        return this._state.get('additionalEmailAddresses');
    }

    get phoneNumber(): ?PhoneNumber {
        return this._state.get('phoneNumber');
    }

    get additionalPhoneNumbers(): Immutable.List<PhoneNumber> {
        return this._state.get('additionalPhoneNumbers');
    }

    setHiddenModelProperties(hiddenModelProperties: Immutable.List<string>) {
        return this.clone(
            this._state.set('hiddenModelProperties', hiddenModelProperties)
        );
    }

    getHiddenModelProperties(): Immutable.List<string> {
        return this._state.get('hiddenModelProperties');
    }

    isHidden(key: string) {
        const hiddenModelProperties = this.getHiddenModelProperties();
        const [groupKey] = key.split('.');
        return (
            (hiddenModelProperties && hiddenModelProperties.includes(key)) ||
            (hiddenModelProperties && hiddenModelProperties.includes(groupKey))
        );
    }

    get address(): StructuredAddress {
        return this._state.get('address');
    }

    @validateAll
    setAllValidationErrors(): this {
        let { address, documents } = this;
        if (address) {
            address = address.setAllValidationErrors();
        } else {
            //validation when address completely is empty
            address = StructuredAddress.fromJS({}).setAllValidationErrors();
        }
        if (documents) {
            documents = documents.map(doc => doc.setAllValidationErrors());
        }
        return this.setAddress(address).setDocuments(documents);
    }

    getCellValidationMessage({ header, validationData }) {
        return EntityImportDataHelper.getCellValidationMessage({
            entity: this,
            header,
            validationData
        });
    }

    sanitize({ contact = true }: Object = {}): this {
        const { contact: contactValue } = this;
        let updatedTransporter = this;

        if (contact && contactValue) {
            updatedTransporter = super.setContact(null);
        }

        return updatedTransporter;
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        const sanitizedTransporter = super.defaultSanitize();
        if (sanitizedTransporter) {
            const sanitizedContact = sanitizedTransporter.contact?.defaultSanitize();
            const sanitizedPhoneNumber = sanitizedTransporter.phoneNumber?.defaultSanitize();
            const sanitizedEmailAddress = sanitizedTransporter.emailAddress?.defaultSanitize();
            const sanitizedDocuments = sanitizedTransporter.documents
                .map(doc => doc.defaultSanitize())
                .filter(doc => !DataUtils.isEmpty(doc.imageUrl));
            return sanitizedTransporter
                .setContact(sanitizedContact)
                .setPhoneNumber(sanitizedPhoneNumber)
                .setEmailAddress(sanitizedEmailAddress)
                .setDocuments(sanitizedDocuments);
        }
        return sanitizedTransporter;
    }

    @validate
    setAdditionalPhoneNumbers(
        additionalPhoneNumbers: Immutable.List<PhoneNumber>
    ): this {
        return super.setAdditionalPhoneNumbers(additionalPhoneNumbers);
    }

    validateAdditionalPhoneNumbers() {
        return this.additionalPhoneNumbers
            .find(
                phoneNumber =>
                    !DataUtils.isEmpty(phoneNumber.phoneNumber) &&
                    phoneNumber.validatePhoneNumber()
            )
            ?.validatePhoneNumber();
    }

    static defaultInit({ props }): InternalMasterTransporter {
        const phoneNumber = PhoneNumber.defaultInit({ props });
        const emailAddress = EmailAddress.defaultInit({ props });
        const contact = Contact.defaultInit({ props });
        const address = StructuredAddress.fromJS({
            countryCode: props.defaultCountryCode
        });

        return InternalMasterTransporter.fromJS({
            isActive: true
        })
            .setContact(contact)
            .setPhoneNumber(phoneNumber)
            .setEmailAddress(emailAddress)
            .setAddress(address);
    }

    get documents(): Immutable.List<Document> {
        return this._state.get('documents');
    }

    get status(): StatusEnum {
        return this._state.get('status');
    }
}

export { InternalMasterTransporter as MasterTransporter };
